import { useHistory } from "react-router";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { Button, Card, Form, Space } from "antd";
import { Prompt } from "react-router-dom";
import * as promotionAPI from "services/promotion";
import { DATETIME_FORMAT_WITH_00_SECOND, LEAVE_CONFIRMATION } from "../../../../constants/common";
import { PromotionForm, PromotionSource } from "../components/PromotionForm";
import { message } from "../../../../components";
import { PromotionWithBuyOneGiftOneFormData, PromotionWithRecurlyFormData } from "../../../../types/model/promotion";
import { transformDescriptionDisclosure } from "../util";
import { usePromotionPageStore } from "../usePromotionPageStore";

function AddNewPromotionPage() {
  const history = useHistory();
  const [form] = Form.useForm();
  const descriptionDisclosures = usePromotionPageStore((state) => state.descriptionDisclosures);
  const [formIsHalfFilledOut, setFormIsHalfFilledOut] = useState(true);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  useEffect(() => {
    // Clear descriptionDisclosures when component mounts
    usePromotionPageStore.setState({ descriptionDisclosures: [] });
  }, []);

  function createPromotionHandler<T>(fetcher: (promotion: T) => Promise<any>, values: T) {
    setIsSaveLoading(true);
    fetcher(values)
      .then(() => {
        setFormIsHalfFilledOut(false);
        form.resetFields();
        history.push("/marketing/promotion");
        message.success("Promotion added successfully.").then();
      })
      .finally(() => setIsSaveLoading(false));
  }

  const handleCreate = () =>
    form.validateFields().then((values) => {
      if (values.source === PromotionSource.RECURLY) {
        if ((values.appliedBillingPlanIds?.length ?? 0) !== descriptionDisclosures.length) {
          message.warning("A Description & Disclosure must be added for every billing plan selected.").then();
          return;
        }
        createPromotionHandler<PromotionWithRecurlyFormData>(promotionAPI.createPromotionWithRecurly, {
          ...values,
          descriptionDisclosures,
        });
      }
      if (values.source === PromotionSource.BUY_ONE_GIFT_ONE) {
        createPromotionHandler<PromotionWithBuyOneGiftOneFormData>(promotionAPI.createPromotionWithBuyOneGiftOne, {
          ...values,
          description: transformDescriptionDisclosure(values, "DescriptionContent"),
          disclosure: transformDescriptionDisclosure(values, "DisclosureContent"),
          timeRange: values.timeRange.map((date: moment.Moment) => date.format(DATETIME_FORMAT_WITH_00_SECOND)),
        });
      }
    });

  const handleCancel = () => {
    usePromotionPageStore.setState({ descriptionDisclosures: [] });
    history.push("/marketing/promotion");
  };

  return (
    <Card
      title="Add new promotion"
      extra={
        <Space size={10}>
          <Button type="default" shape="round" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="primary" shape="round" onClick={handleCreate} loading={isSaveLoading}>
            Save
          </Button>
        </Space>
      }
      className="page-container"
    >
      <Prompt when={formIsHalfFilledOut} message={LEAVE_CONFIRMATION} />
      <PromotionForm form={form} />
    </Card>
  );
}

export default AddNewPromotionPage;
